import { mediaSchema } from "@/features/dashboard/types/media.ts";
import { userSchema } from "@/features/dashboard/types/user.ts";
import { z } from "zod";

const SheriffAttemptDate = z.object({
	attempted_at: z.string().optional().nullable(),
});

const ActivitySchema = z.object({
	id: z.string(),
	case_id: z.string(),
	stage: z.string(),
	activity_id: z.string().optional().nullable(),
	activity: z.string().max(255, {
		message: "Title must not be longer than 255 characters.",
	}),
	assigned_to: z.string(),
	due_at: z.any().optional().nullable(),
	status: z.string().optional().nullable(),
	hearing_status: z.string().optional().nullable(),
	previous_due_at: z.string().optional().nullable(),
	created_at: z.string(),
	in_calendar: z.boolean().default(false),
	remarks: z.string().optional().nullable(),
	hearing_mode: z.string().optional(),
	author: z.object({}),
});

const caseActivitySchema = z.object({
	id: z.string(),
	case_id: z.string(),
	stage: z.string(),
	activity_id: z.string().optional().nullable(),
	activity: z.string().max(255, {
		message: "Title must not be longer than 255 characters.",
	}),
	assigned_to: z.string(),
	with_fees: z.boolean().optional().nullable(),
	with_grant: z.boolean().optional().nullable(),
	is_visible: z.boolean().optional().nullable(),
	with_compliance: z.boolean().optional().nullable(),
	with_deadline: z.boolean().optional().nullable(),
	shared_with: z.any().optional().nullable(),
	due_at: z.any().optional().nullable(),
	decision_at: z.any().optional().nullable(),
	status: z.string().optional().nullable(),
	tag: z.string().optional().nullable(),
	author: userSchema.optional().nullable(),
	hearing_status: z.string().optional().nullable(),
	previous_due_at: z.string().optional().nullable(),
	attachments: z.array(mediaSchema).optional().nullable(),
	remarks: z.string().optional().nullable(),
	in_calendar: z.boolean().default(false),
	created_at: z.string(),
	hearing_mode: z.string().optional(),
	state: z.string().optional().nullable(),
	exhibits: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
				parties: z.string().optional().nullable(),
				witness_name: z.string().optional().nullable(),
			}),
		)
		.optional(),
	witness_name: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
			}),
		)
		.optional(),
	replies: z.array(ActivitySchema).optional().nullable(),
});

export const caseActivityProofFormSchema = z
	.object({
		id: z.string().optional(),
		case_id: z.string(),
		attachment: z
			.array(z.instanceof(File), {
				required_error: "Required",
			})
			.optional()
			.nullable(),
	})
	.superRefine((data, ctx) => {
		const { attachment } = data;
		if (attachment?.length === 0 || !attachment) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["attachment"],
				message: "Attachment is required",
			});
			return z.NEVER;
		}
	});

export const caseActivityFormSchema = z
	.object({
		id: z.string().optional(),
		format_type: z.string(),
		action_type: z.string().optional().nullable(),
		counter_claim: z.string().optional().nullable(),
		manner_of_service: z.string().optional().nullable(),
		mode_of_dismissal: z.string().optional().nullable(),
		type: z.string().optional().nullable(),
		case_id: z.string(),
		stage: z.string(),
		proof_of_payment: z.any(),
		evidences: z.any(),
		state: z.string().default("submitted"),
		document_key: z.string().optional().nullable(),
		calendar_event: z.string().optional().nullable(),
		activity_id: z.string().optional().nullable(),
		move_back: z.string().optional().nullable(),
		summons: z.string().optional().nullable(),
		status: z.string().optional().nullable(),
		hearing_status: z.string().optional().nullable(),
		party_type: z.string().optional().nullable(),
		settlement_report: z.string().optional().nullable(),
		activity: z.string().max(255, {
			message: "Title must not be longer than 255 characters.",
		}),
		assigned_to: z.string(),
		due_at: z.any().optional().nullable(),
		scheduled_at: z.any().optional().nullable(),
		service_status: z.string().optional().nullable(),
		service_reason: z.string().optional().nullable(),
		sheriff_report: z.any().optional().nullable(),
		defendants: z.any().optional().nullable(),
		sheriff_attempts: z.any().optional().nullable(),
		service_date: z.any().optional().nullable(),
		service_attempt_date: z.any().optional().nullable(),
		tag: z.any().optional().nullable(),
		decision_at: z.any().optional().nullable(),
		ordered_at: z.any().nullable().optional(),
		filed_at: z.any().nullable().optional(),
		submitted_at: z.any().optional().nullable(),
		next_stage: z.any().optional().nullable(),
		form_template_id: z.any().optional().nullable(),
		previous_due_at: z.string().optional().nullable(),
		hearings: z.any().optional().nullable(),
		amount: z.any().optional().nullable(),
		in_calendar: z.boolean().default(false),
		with_compliance: z.boolean().default(false),
		with_deadline: z.boolean().default(false),
		with_grant: z.boolean().default(false),
		with_fees: z.boolean().default(false),
		with_decision: z.boolean().default(false),
		respondents: z.any().nullable().optional(),
		decision: z.boolean().default(false),
		remarks: z.string().optional().nullable(),
		move_to_next_stage: z.boolean().optional().nullable(),
		close_case: z.boolean().optional().nullable(),
		replies: z.array(ActivitySchema).optional().nullable(),
		case_document: z.instanceof(File).optional(),
		hearing_mode: z.string().optional(),
		attempts: z.array(SheriffAttemptDate).optional().nullable(),
		exhibits: z
			.array(
				z.object({
					label: z.string().optional().nullable(),
					value: z.string().optional().nullable(),
					parties: z.string().optional().nullable(),
					witness_name: z.string().optional().nullable(),
				}),
			)
			.optional(),
		witness_name: z
			.array(
				z.object({
					label: z.string().optional().nullable(),
					value: z.string().optional().nullable(),
					parties: z.string().optional().nullable(),
				}),
			)
			.optional(),
		attachment: z
			.array(z.instanceof(File), {
				required_error: "Required",
			})
			.optional()
			.nullable(),
	})
	.superRefine((data, ctx) => {
		const { format_type, attachment } = data;
		if (format_type === "upload_files" && (attachment?.length === 0 || !attachment)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["attachment"],
				message: "Attachment is required",
			});
			return z.NEVER;
		}
	});

export const caseActivityRequiredOrderFormSchema = z
	.object({
		id: z.string().optional(),
		format_type: z.string(),
		action_type: z.string().optional().nullable(),
		counter_claim: z.string().optional().nullable(),
		manner_of_service: z.string().optional().nullable(),
		mode_of_dismissal: z.string().optional().nullable(),
		type: z.string().optional().nullable(),
		case_id: z.string(),
		stage: z.string(),
		proof_of_payment: z.any(),
		evidences: z.any(),
		state: z.string().default("submitted"),
		document_key: z.string().optional().nullable(),
		calendar_event: z.string().optional().nullable(),
		activity_id: z.string().optional().nullable(),
		move_back: z.string().optional().nullable(),
		summons: z.string().optional().nullable(),
		status: z.string().optional().nullable(),
		hearing_status: z.string().optional().nullable(),
		party_type: z.string().optional().nullable(),
		settlement_report: z.string().optional().nullable(),
		activity: z.string().max(255, {
			message: "Title must not be longer than 255 characters.",
		}),
		assigned_to: z.string(),
		due_at: z.any().optional().nullable(),
		scheduled_at: z.any().optional().nullable(),
		service_status: z.string().optional().nullable(),
		service_reason: z.string().optional().nullable(),
		sheriff_report: z.any().optional().nullable(),
		defendants: z.any().optional().nullable(),
		sheriff_attempts: z.any().optional().nullable(),
		service_date: z.any().optional().nullable(),
		service_attempt_date: z.any().optional().nullable(),
		tag: z.any().optional().nullable(),
		decision_at: z.any().optional().nullable(),
		ordered_at: z.string(),
		filed_at: z.any().nullable().optional(),
		submitted_at: z.any().optional().nullable(),
		next_stage: z.any().optional().nullable(),
		form_template_id: z.any().optional().nullable(),
		previous_due_at: z.string().optional().nullable(),
		hearings: z.any().optional().nullable(),
		amount: z.any().optional().nullable(),
		in_calendar: z.boolean().default(false),
		with_compliance: z.boolean().default(false),
		with_deadline: z.boolean().default(false),
		with_grant: z.boolean().default(false),
		with_fees: z.boolean().default(false),
		with_decision: z.boolean().default(false),
		respondents: z.any().nullable().optional(),
		decision: z.boolean().default(false),
		remarks: z.string().optional().nullable(),
		move_to_next_stage: z.boolean().optional().nullable(),
		close_case: z.boolean().optional().nullable(),
		replies: z.array(ActivitySchema).optional().nullable(),
		case_document: z.instanceof(File).optional(),
		hearing_mode: z.string().optional(),
		attempts: z.array(SheriffAttemptDate).optional().nullable(),
		exhibits: z
			.array(
				z.object({
					label: z.string().optional().nullable(),
					value: z.string().optional().nullable(),
					parties: z.string().optional().nullable(),
					witness_name: z.string().optional().nullable(),
				}),
			)
			.optional(),
		witness_name: z
			.array(
				z.object({
					label: z.string().optional().nullable(),
					value: z.string().optional().nullable(),
					parties: z.string().optional().nullable(),
				}),
			)
			.optional(),
		attachment: z
			.array(z.instanceof(File), {
				required_error: "Required",
			})
			.optional()
			.nullable(),
	})
	.superRefine((data, ctx) => {
		const { format_type, attachment, ordered_at } = data;
		if (format_type === "upload_files" && (attachment?.length === 0 || !attachment)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["attachment"],
				message: "Attachment is required",
			});
			return z.NEVER;
		}
	});

export const caseStageFormSchema = z.object({
	id: z.string().optional(),
	stage: z.string(),
});

export const caseRescheduleForm = z.object({
	id: z.string().optional(),
	case_id: z.string(),
	stage: z.string(),
	activity_id: z.string().optional().nullable(),
	move_back: z.string().optional().nullable(),
	summons: z.string().optional().nullable(),
	status: z.string().optional().nullable(),
	hearing_status: z.string().optional().nullable(),
	party_type: z.string().optional().nullable(),
	settlement_report: z.string().optional().nullable(),
	activity: z.string().max(255, {
		message: "Title must not be longer than 255 characters.",
	}),
	assigned_to: z.string(),
	due_at: z.any(),
	attachment: z.any().nullable().optional(),
	scheduled_at: z.any().optional().nullable(),
	service_status: z.string().optional().nullable(),
	service_reason: z.string().optional().nullable(),
	sheriff_report: z.any().optional().nullable(),
	defendants: z.any().optional().nullable(),
	service_date: z.any().optional().nullable(),
	tag: z.any().optional().nullable(),
	decision_at: z.any().optional().nullable(),
	ordered_at: z.any().nullable().optional(),
	filed_at: z.any().nullable().optional(),
	submitted_at: z.any().optional().nullable(),
	next_stage: z.any().optional().nullable(),
	form_template_id: z.any().optional().nullable(),
	previous_due_at: z.string().optional().nullable(),
	hearings: z.any().optional().nullable(),
	amount: z.any().optional().nullable(),
	in_calendar: z.boolean().default(false),
	with_compliance: z.boolean().default(false),
	with_deadline: z.boolean().default(false),
	with_grant: z.boolean().default(false),
	with_fees: z.boolean().default(false),
	with_decision: z.boolean().default(false),
	respondents: z.any().nullable().optional(),
	decision: z.boolean().default(false),
	remarks: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	hearing_mode: z.string().optional().nullable(),
	type: z.string().optional().nullable(),
	move_to_next_stage: z.boolean().optional().nullable(),
	close_case: z.boolean().optional().nullable(),
	replies: z.array(ActivitySchema).optional().nullable(),
	case_document: z.instanceof(File).optional(),
	exhibits: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
				parties: z.string().optional().nullable(),
				witness_name: z.string().optional().nullable(),
			}),
		)
		.optional(),
	witness_name: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
			}),
		)
		.optional(),
});

export const caseHearingSchema = z.object({
	hearing: z.object({
		title: z.string(),
		type: z.string(),
		assigned_to: z.string(),
		due_date: z.string({ message: "Schedule date is required" }),
		remarks: z.string({ message: "Activity/Event is required" }),
		stage: z.string({ message: "Stage is required" }),
		hearing_count: z.number().optional(),
		hearing_mode: z.string().optional(),
	}),
});

export const caseActivityMotionFormSchema = z.object({
	case_id: z.string(),
	stage: z.string(),
	action_type: z.string(),
	activity_id: z.string().optional().nullable(),
	move_back: z.string().optional().nullable(),
	status: z.string().optional().nullable(),
	party_type: z.string().optional().nullable(),
	settlement_report: z.string().optional().nullable(),
	activity: z.string(),
	assigned_to: z.string(),
	with_grant: z.boolean().default(false),
	remarks: z.string().optional().nullable(),
	posting_for: z.any(),
	files: z.any(),
	evidences: z.any(),
	defendants: z.any(),
});

const activityTitleSchema = z.object({
	id: z.string(),
	activity: z.string(),
});

export const activityDeleteSchema = z.object({
	id: z.string(),
	reason: z.string().min(1, { message: "Required" }),
	password: z.string().min(6, { message: "Required" }),
});

export const activityNotifySchema = z.object({
	id: z.string(),
	external_emails: z.array(z.string().email()).optional().nullable(),
	password: z.string().min(6, { message: "Required" }),
});

export type CaseActivityNotifySchema = z.infer<typeof activityNotifySchema>;
export type CaseActivityDeleteSchema = z.infer<typeof activityDeleteSchema>;
export type CaseActivityTitleSchema = z.infer<typeof activityTitleSchema>;
export type CaseActivity = z.infer<typeof caseActivitySchema>;
export type CaseActivityForm = z.infer<typeof caseActivityFormSchema>;
export type CaseStageForm = z.infer<typeof caseStageFormSchema>;
export type CaseHearing = z.infer<typeof caseHearingSchema>;
export type CaseActivityMotionForm = z.infer<typeof caseActivityMotionFormSchema>;
export type CaseActivityProofFormSchema = z.infer<typeof caseActivityProofFormSchema>;
export type CaseRescheduleForm = z.infer<typeof caseRescheduleForm>;
export type CaseActivityRequiredOrderFormSchema = z.infer<typeof caseActivityRequiredOrderFormSchema>;
